import { Injectable } from "@angular/core";
import { CreateBookingWS } from "@app/core/booking/data/network/create-booking.ws";
import { DeleteBookingWS } from "@app/core/booking/data/network/delete-booking.ws";
import { EditBookingWS } from "@app/core/booking/data/network/edit-booking.ws";
import { GetBookingWS } from "@app/core/booking/data/network/get-booking.ws";
import { GetBookingsWS } from "@app/core/booking/data/network/get-bookings.ws";
import { BookingRepo } from "@app/core/booking/domain/booking-repo";
import { Booking } from "@app/core/booking/domain/model/booking";
import { BookingDetail } from "@app/core/booking/domain/model/booking-detail";

import { Observable } from "rxjs";
import { ApplyBookingPaymentWS } from "@app/core/booking/data/network/apply-booking-payment.ws";
import { ApplyCouponWS } from "@app/core/booking/data/network/apply-coupon.ws";
import { UnapplyCouponWS } from "@app/core/booking/data/network/unapply-coupon.ws";
import { ConfirmPaymentDoneWS } from "@app/core/booking/data/network/confirm-payment-done.ws";
import { PaymentState } from "@app/core/booking/domain/model/payment-state";
import { JoinBookingSplitPaidWS } from "@app/core/booking/data/network/join-booking-split-paid.ws";
import { UpdateConsentWS } from "@app/core/booking/data/network/update-consent.ws";
import { PaymentMethodType } from "@app/core/shared/domain/payment-method-type";
import { MatchSearchCriteria } from "@app/core/match/domain/match-search-criteria";
import { CompetitiveWS } from "../network/competitive.ws";

@Injectable()
export class BookingDataRepo implements BookingRepo {
  constructor(
    private createBookingWS: CreateBookingWS,
    private editBookingWS: EditBookingWS,
    private getBookingsWS: GetBookingsWS,
    private getBookingWS: GetBookingWS,
    private deleteBookingWS: DeleteBookingWS,
    private applyPaymentWS: ApplyBookingPaymentWS,
    private applyCouponWS: ApplyCouponWS,
    private unapplyCouponWS: UnapplyCouponWS,
    private confirmPaymentDoneWS: ConfirmPaymentDoneWS,
    private joinBookingSplitPaidWS: JoinBookingSplitPaidWS,
    private updateConsentWS: UpdateConsentWS,
    private competitive: CompetitiveWS
  ) {}

  createBooking(booking: Booking, token: string): Observable<void> {
    return this.createBookingWS.execute(booking, token);
  }

  editBooking(
    booking: Booking,
    bookingId: string,
    token: string
  ): Observable<void> {
    return this.editBookingWS.execute(booking, bookingId, token);
  }

  getBookings(
    matchCriteria: MatchSearchCriteria,
    token: string
  ): Observable<BookingDetail[]> {
    return this.getBookingsWS.execute(matchCriteria, token);
  }

  getBooking(bookingId: string, token: string): Observable<Booking> {
    return this.getBookingWS.execute(bookingId, token);
  }

  deleteBooking(bookingId: string, token: string): Observable<void> {
    return this.deleteBookingWS.execute(bookingId, token);
  }

  updateConsent(
    bookingId: string,
    consent: boolean,
    token: string
  ): Observable<void> {
    return this.updateConsentWS.execute(bookingId, consent, token);
  }

  applyPayment(
    bookingId: string,
    paymentType: string,
    paymentMethodType: PaymentMethodType,
    token: string
  ): Observable<Booking> {
    return this.applyPaymentWS.execute(
      bookingId,
      paymentType,
      paymentMethodType,
      token
    );
  }
  applyCoupon(
    bookingId: string,
    couponCode: string,
    token: string
  ): Observable<Booking> {
    return this.applyCouponWS.execute(bookingId, couponCode, token);
  }

  unapplyCoupon(
    bookingId: string,
    couponCode: string,
    token: string
  ): Observable<Booking> {
    return this.unapplyCouponWS.execute(bookingId, couponCode, token);
  }

  confirmPaymentDone(
    bookingId: string,
    token: string
  ): Observable<PaymentState> {
    return this.confirmPaymentDoneWS.execute(bookingId, token);
  }

  join(
    bookingId: string,
    paymentOption: string,
    token: string
  ): Observable<Booking> {
    return this.joinBookingSplitPaidWS.execute(bookingId, token);
  }

  setCompetitive(
    pk: string,
    competitive: boolean,
    token: string
  ): Observable<void> {
    return this.competitive.execute(pk, competitive, token);
  }
}
